import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const sections = {
  en: [
    {
      title: '1. Scope & Provider',
      content: 'These Terms of Service govern your use of the Stup-On platform (stup-on.de) operated by Stup-On, hello@stup-on.de. By registering or using our services, you agree to these terms in full.'
    },
    {
      title: '2. Services Provided',
      content: 'Stup-On provides a digital learning and consulting platform offering:\n\n• A structured 10-step startup methodology\n• Interactive tools (Strategy Canvas, etc.)\n• Access to 1:1 mentoring sessions (included in the Platform Access plan)\n• Optional MVP co-creation service\n\nThe scope of services depends on the plan purchased.'
    },
    {
      title: '3. Registration & Account',
      content: 'You must be at least 18 years old to register. You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately of any unauthorised use of your account at hello@stup-on.de.\n\nWe reserve the right to suspend or terminate accounts that violate these terms.'
    },
    {
      title: '4. Prices & Payment',
      content: 'All prices are stated in EUR and include applicable VAT. Payment is processed via Stripe. By completing a purchase, you authorise us to charge the stated amount.\n\nCurrent pricing:\n• Platform Access: €49.99 (one-time)\n• MVP Co-Creation: €499.90 (one-time)\n\nPrices may change. Existing purchases are not affected by price changes.'
    },
    {
      title: '5. Right of Withdrawal (EU Consumers)',
      content: 'As an EU consumer, you have the right to withdraw from a distance contract within 14 days without giving a reason.\n\nHowever, by accessing digital content before the withdrawal period expires, you expressly consent to immediate delivery and acknowledge that your right of withdrawal is thereby waived (§ 356 Abs. 5 BGB / Art. 16(m) Consumer Rights Directive).\n\nTo exercise withdrawal before accessing content, contact: hello@stup-on.de'
    },
    {
      title: '6. Intellectual Property',
      content: 'All content on Stup-On – including text, graphics, tools, and methodology – is the intellectual property of Stup-On and is protected by copyright law. You may not reproduce, distribute, or create derivative works without prior written consent.\n\nYou retain ownership of any content you create using our tools (e.g., your Strategy Canvas data).'
    },
    {
      title: '7. Acceptable Use',
      content: 'You agree not to:\n\n• Share your account credentials with others\n• Attempt to reverse-engineer or scrape the platform\n• Use the platform for unlawful purposes\n• Upload harmful, defamatory, or infringing content\n• Circumvent technical protection measures'
    },
    {
      title: '8. Limitation of Liability',
      content: 'To the maximum extent permitted by law, Stup-On is not liable for indirect, incidental, or consequential damages arising from your use of the platform.\n\nOur total liability is limited to the amount you paid for the service in the 12 months preceding the claim. Nothing in these terms limits liability for gross negligence, wilful misconduct, or death/personal injury caused by our negligence.'
    },
    {
      title: '9. Governing Law & Dispute Resolution',
      content: 'These terms are governed by German law. The place of jurisdiction is Germany.\n\nFor consumer disputes within the EU, the European Commission provides an online dispute resolution platform: https://ec.europa.eu/consumers/odr\n\nWe are not obliged to participate in alternative dispute resolution proceedings, but we are willing to seek amicable solutions.'
    },
    {
      title: '10. Changes to Terms',
      content: 'We reserve the right to modify these Terms of Service. We will notify you at least 30 days before changes take effect via email. Continued use of the platform after changes constitutes acceptance of the new terms.'
    }
  ],
  de: [
    {
      title: '1. Geltungsbereich & Anbieter',
      content: 'Diese Nutzungsbedingungen regeln die Nutzung der Stup-On-Plattform (stup-on.de), betrieben von Stup-On, hello@stup-on.de. Mit der Registrierung oder Nutzung unserer Dienste stimmst du diesen Bedingungen vollständig zu.'
    },
    {
      title: '2. Angebotene Leistungen',
      content: 'Stup-On bietet eine digitale Lern- und Beratungsplattform mit:\n\n• Einer strukturierten 10-Schritte-Startup-Methodik\n• Interaktiven Tools (Strategy Canvas etc.)\n• Zugang zu 1:1 Mentoring-Sessions (im Platform-Access-Paket enthalten)\n• Optionalem MVP-Co-Creation-Service\n\nDer Leistungsumfang richtet sich nach dem gebuchten Paket.'
    },
    {
      title: '3. Registrierung & Konto',
      content: 'Du musst mindestens 18 Jahre alt sein, um dich zu registrieren. Du bist für die Vertraulichkeit deiner Zugangsdaten verantwortlich. Unbefugte Nutzung deines Kontos musst du uns unverzüglich unter hello@stup-on.de melden.\n\nWir behalten uns vor, Konten bei Verstößen gegen diese Bedingungen zu sperren oder zu löschen.'
    },
    {
      title: '4. Preise & Zahlung',
      content: 'Alle Preise sind in EUR angegeben und verstehen sich inklusive der gesetzlichen Mehrwertsteuer. Die Zahlung erfolgt über Stripe.\n\nAktuelle Preise:\n• Platform Access: 49,99 € (einmalig)\n• MVP Co-Creation: 499,90 € (einmalig)\n\nPreisänderungen gelten nicht für bereits getätigte Käufe.'
    },
    {
      title: '5. Widerrufsrecht (EU-Verbraucher)',
      content: 'Als EU-Verbraucher hast du das Recht, innerhalb von 14 Tagen ohne Angabe von Gründen von einem Fernabsatzvertrag zurückzutreten.\n\nDurch den Zugriff auf digitale Inhalte vor Ablauf der Widerrufsfrist stimmst du jedoch der sofortigen Lieferung ausdrücklich zu und bestätigst, dass dein Widerrufsrecht damit erlischt (§ 356 Abs. 5 BGB).\n\nUm den Widerruf vor dem Zugriff auf Inhalte auszuüben, kontaktiere: hello@stup-on.de'
    },
    {
      title: '6. Geistiges Eigentum',
      content: 'Alle Inhalte auf Stup-On – einschließlich Texte, Grafiken, Tools und Methodik – sind geistiges Eigentum von Stup-On und urheberrechtlich geschützt. Vervielfältigung, Verbreitung oder Bearbeitung ohne schriftliche Genehmigung ist untersagt.\n\nDu behältst das Eigentum an Inhalten, die du mit unseren Tools erstellst (z. B. deine Strategy-Canvas-Daten).'
    },
    {
      title: '7. Erlaubte Nutzung',
      content: 'Du verpflichtest dich, folgendes zu unterlassen:\n\n• Weitergabe deiner Zugangsdaten\n• Reverse Engineering oder Scraping der Plattform\n• Nutzung für rechtswidrige Zwecke\n• Upload von schädlichen, diffamierenden oder rechtsverletzenden Inhalten\n• Umgehung technischer Schutzmaßnahmen'
    },
    {
      title: '8. Haftungsbeschränkung',
      content: 'Stup-On haftet im gesetzlich zulässigen Umfang nicht für mittelbare, zufällige oder Folgeschäden.\n\nUnsere Gesamthaftung ist auf den Betrag begrenzt, den du in den 12 Monaten vor dem Anspruch gezahlt hast. Dies gilt nicht für grobe Fahrlässigkeit, Vorsatz oder Schäden aus der Verletzung von Leben, Körper oder Gesundheit.'
    },
    {
      title: '9. Anwendbares Recht & Streitbeilegung',
      content: 'Es gilt deutsches Recht. Gerichtsstand ist Deutschland.\n\nFür Verbraucherstreitigkeiten innerhalb der EU stellt die Europäische Kommission eine Online-Streitbeilegungsplattform bereit: https://ec.europa.eu/consumers/odr\n\nWir sind nicht verpflichtet, an Schlichtungsverfahren teilzunehmen, sind jedoch bereit, einvernehmliche Lösungen zu suchen.'
    },
    {
      title: '10. Änderungen der Bedingungen',
      content: 'Wir behalten uns vor, diese Nutzungsbedingungen zu ändern. Wir informieren dich mindestens 30 Tage vor Inkrafttreten der Änderungen per E-Mail. Die weitere Nutzung der Plattform nach den Änderungen gilt als Zustimmung.'
    }
  ]
};

const TermsOfService = () => {
  const { lang } = useLanguage();
  const isDE = lang === 'de';

  return (
    <div className="bg-white py-16 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-10">
            <span className="inline-block bg-purple-50 text-purple-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Legal</span>
            <h1 className="text-4xl font-black text-gray-900 mb-3">
              {isDE ? 'Nutzungsbedingungen' : 'Terms of Service'}
            </h1>
            <p className="text-gray-500 text-sm">
              {isDE ? 'Zuletzt aktualisiert: Januar 2025' : 'Last updated: January 2025'}
            </p>
          </div>

          <div className="space-y-8">
            {sections[lang].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border-b border-gray-100 pb-8 last:border-0"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;